

import React from 'react';
import { FileText, Edit2, Save, Download, CheckCircle, XCircle, User, GraduationCap, FileBadge, CalendarClock, Eye } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const SectionTitle = ({ title, icon }) => (
  <h4 className="font-bold text-slate-800 text-[11px] uppercase flex items-center gap-2 mb-3 mt-5 pb-1 border-b border-slate-200">
    {icon} {title}
  </h4>
);

const DataRow = ({ label, value, isEdit, name, type = "text", valEdit, onChange, children }) => (
  <div className="mb-3">
    <label className="text-slate-500 text-[9px] font-bold uppercase block mb-1">{label}</label>
    {!isEdit ? (
      <div className="font-medium text-slate-800 text-sm bg-slate-50 px-3 py-2 rounded border border-transparent min-h-[36px] flex items-center">{value || '-'}</div>
    ) : children ? (
      children
    ) : (
      <input type={type} name={name} value={valEdit || ''} onChange={onChange} className="w-full border border-slate-300 rounded px-3 py-2 text-sm outline-none focus:border-blue-500" />
    )}
  </div>
);

const CustomDatePicker = ({ name, value, onChange }) => {
  const parseDate = (str) => {
    if (!str) return null;
    const parts = str.split('-');
    if (parts.length === 3) return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    return null;
  };

  return (
    <DatePicker
      selected={parseDate(value)}
      onChange={(date) => {
        if (!date) {
           onChange({ target: { name, value: '' } });
           return;
        }
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        onChange({ target: { name, value: `${y}-${m}-${d}` } });
      }}
      onChangeRaw={(e) => e.preventDefault()} 
      dateFormat="dd/MM/yyyy"
      placeholderText="Pilih Tanggal 📅"
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      wrapperClassName="w-full"
      className="w-full border border-slate-300 rounded px-3 py-2 text-sm outline-none focus:border-blue-500 cursor-pointer"
    />
  );
};

const safeParseDate = (inputDate) => {
    if (!inputDate) return null;
    if (inputDate instanceof Date) {
        if (isNaN(inputDate.getTime())) return null;
        return { year: inputDate.getFullYear(), month: inputDate.getMonth() + 1, day: inputDate.getDate() };
    }
    const dateStr = String(inputDate).trim();
    if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length >= 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10); 
            const year = parseInt(parts[2], 10);
            if (!isNaN(day) && !isNaN(month) && !isNaN(year)) return { year, month, day };
        }
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
    if (dateStr.includes('-')) {
        const parts = dateStr.split('T')[0].split('-');
        if (parts.length >= 3) {
            let year = parseInt(parts[0], 10);
            let month = parseInt(parts[1], 10); 
            let day = parseInt(parts[2], 10);
            if (year < 1000) { 
                day = parseInt(parts[0], 10);
                year = parseInt(parts[2], 10); 
            }
            if (!isNaN(day) && !isNaN(month) && !isNaN(year)) return { year, month, day };
        }
    }
    return null;
};

const formatDateStr = (dateStr) => {
    const parsed = safeParseDate(dateStr);
    if (!parsed) return '-';
    return `${String(parsed.day).padStart(2, '0')}/${String(parsed.month).padStart(2, '0')}/${parsed.year}`;
};

const getIsoDate = (dateStr) => {
    const parsed = safeParseDate(dateStr);
    if (!parsed) return '';
    return `${parsed.year}-${String(parsed.month).padStart(2, '0')}-${String(parsed.day).padStart(2, '0')}`;
};

const DetailPegawaiCard = ({ 
  selectedItem, isEditingBio, setIsEditingBio, editedBio, setEditedBio, // <-- Tambahkan ini
  onBioChange, onSKPChange, skpMapData, isReadOnly, onSaveBio, onReject, onApprove, golonganOptions,
  onPreviewClick, userRole = ''
}) => {
  
  const getVal = (field) => editedBio[field] !== undefined ? editedBio[field] : (selectedItem[field] || '');
  const currentData = isEditingBio ? editedBio : selectedItem;
const skpList = Array.from({ length: getVal('is_skp_2026') === 'Ya' ? 4 : 3 }).map((_, i) => (currentData.skp_data && currentData.skp_data[i]) ? currentData.skp_data[i] : { tahun: '', kode: '', sudah_pak: false });  const isIntegrasi = (getVal('jenis_pak') || 'Integrasi').toLowerCase() === 'integrasi';
  const rawGolongan = getVal('golongan');
  const golonganPegawai = String(rawGolongan).toLowerCase().replace(/\s/g, '');
  const isJabatanTidakSesuai = getVal('is_jabatan_sesuai') === 'Tidak';
  
  const parsedSelesai = safeParseDate(getVal('masa_penilaian_selesai'));
  const parsedTmtBaru = safeParseDate(getVal('tmt_jabatan_baru'));
  const parsedTmtAsli = safeParseDate(getVal('tmt_jabatan')); 
  
  const isNonGuru = getVal('jenis_jabatan') === 'Non-Guru';
  const isGol3A = golonganPegawai === 'iii/a' || golonganPegawai === 'iiia';
  const isBaseGol = ['iii/a', 'iii/c', 'iv/a', 'iv/d'].includes(golonganPegawai);
  const isGolKhususReset = ['iii/c', 'iv/a', 'iv/d'].includes(golonganPegawai) && !isIntegrasi;

  const parsedTmtGol = safeParseDate(getVal('tmt_golongan'));
  const isTmtGol2425 = parsedTmtGol && (parsedTmtGol.year >= 2024);

  const rawAkLama = String(getVal('ak_lama') || '0').replace(',', '.');
  let displayAkLama = Number(rawAkLama) || 0;
  
  if (isGolKhususReset && (getVal('is_baru_naik_jenjang') === 'Ya' || isTmtGol2425)) {
      displayAkLama = 0;
  }

  const getDisplayMulai = () => {
    if (!isEditingBio && isIntegrasi) {
        const is4896 = Math.abs(displayAkLama - 48.96) < 0.01;
        if (is4896 && parsedTmtAsli) {
            return `01/${String(parsedTmtAsli.month).padStart(2,'0')}/${parsedTmtAsli.year}`;
        }
        return '01/01/2023';
    }
    return formatDateStr(selectedItem.masa_penilaian_mulai);
  };

  const getDisplaySelesai = () => {
    if (!isEditingBio && isIntegrasi) return '31/12/2025';
    return formatDateStr(selectedItem.masa_penilaian_selesai);
  };

  // Fungsi untuk menangani perubahan golongan secara otomatis di Detail Pegawai
 const handleGolonganChangeLocal = (e) => {
    const selectedGol = e.target.value;
    const isGuru = getVal('jenis_jabatan') !== 'Non-Guru';
    
    let autoJabatan = getVal('jabatan');
    if (isGuru) {
      if (['III/a', 'III/b', 'iii/a', 'iii/b'].includes(selectedGol.toLowerCase())) autoJabatan = 'Guru Ahli Pertama';
      else if (['III/c', 'III/d', 'iii/c', 'iii/d'].includes(selectedGol.toLowerCase())) autoJabatan = 'Guru Ahli Muda';
      else if (['IV/a', 'IV/b', 'IV/c', 'iv/a', 'iv/b', 'iv/c'].includes(selectedGol.toLowerCase())) autoJabatan = 'Guru Ahli Madya';
      else if (['IV/d', 'IV/e', 'iv/d', 'iv/e'].includes(selectedGol.toLowerCase())) autoJabatan = 'Guru Ahli Utama';
    }

    // Jika setEditedBio tersedia dari parent, gunakan untuk update masal
    if (setEditedBio) {
      setEditedBio(prev => ({
        ...prev,
        golongan: selectedGol,
        jabatan: autoJabatan,
        is_jabatan_sesuai: 'Ya',
        jabatan_baru: '',
        tmt_jabatan_baru: '',
        is_baru_naik_jenjang: 'Tidak'
      }));
    } else {
      // Fallback jika lupa dipasang di parent
      onBioChange(e);
    }
  };
  return (
    <div className="w-full space-y-4">
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative">
        <div className="flex justify-between items-center mb-2 border-b pb-3">
          <h3 className="font-bold text-slate-800 flex items-center gap-2"><FileText size={18} className="text-blue-600"/> Data Usulan</h3>
          {!isReadOnly && (
            !isEditingBio && (
              <button onClick={() => setIsEditingBio(true)} className="text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded text-xs font-bold transition-colors flex gap-1">
                <Edit2 size={14} /> Edit Data
              </button>
            )
          )}
        </div>

        <div className="space-y-1 text-sm max-h-[70vh] overflow-y-auto pr-3 custom-scrollbar">
          
          <SectionTitle title="Data Pengusulan & PAK" icon={<CalendarClock size={16} className="text-blue-500"/>} />
          <DataRow label="JENIS PAK TERAKHIR" isEdit={isEditingBio} value={selectedItem.jenis_pak}>
            <select name="jenis_pak" value={getVal('jenis_pak') || 'Integrasi'} onChange={onBioChange} className="w-full border rounded p-2 text-sm">
              <option value="Integrasi">Integrasi</option><option value="Konversi">Konversi</option>
            </select>
          </DataRow>
          
          <DataRow label="Nilai PAK Terakhir" isEdit={isEditingBio} type="number" name="ak_lama" valEdit={getVal('ak_lama')} onChange={onBioChange} value={<span className="font-bold text-emerald-600 text-base">{displayAkLama}</span>} />

          <div className="mb-3">
            <label className="text-slate-500 text-[9px] font-bold uppercase block mb-1 text-center bg-blue-50 py-1 rounded">PERIODE PENILAIAN</label>
            <div className="grid grid-cols-2 gap-3 mt-2">
                <DataRow label="Mulai" isEdit={isEditingBio} name="masa_penilaian_mulai" value={getDisplayMulai()} >
                   <CustomDatePicker name="masa_penilaian_mulai" value={getIsoDate(getVal('masa_penilaian_mulai'))} onChange={onBioChange} />
                </DataRow>
                <DataRow label="Selesai" isEdit={isEditingBio} name="masa_penilaian_selesai" value={getDisplaySelesai()} >
                   <CustomDatePicker name="masa_penilaian_selesai" value={getIsoDate(getVal('masa_penilaian_selesai'))} onChange={onBioChange} />
                </DataRow>
            </div>
          </div>

      <div className="mb-3">
            <div className="flex justify-between items-end mb-2">
              <label className="text-slate-500 text-[10px] font-bold uppercase block">Riwayat SKP</label>
              
              {/* UI TAMBAHAN: CHECKBOX SKP 2026 */}
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-2 py-1 rounded">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input 
                    type="checkbox" 
                    name="is_skp_2026" 
                    checked={getVal('is_skp_2026') === 'Ya'} 
                    onChange={(e) => onBioChange({target: {name: 'is_skp_2026', value: e.target.checked ? 'Ya' : 'Tidak'}})} 
                    disabled={!isEditingBio}
                    className="w-3 h-3 text-blue-600"
                  />
                  <span className="text-[10px] font-bold text-blue-800">Ada SKP 2026</span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              {skpList.map((skp, idx) => {
                const currentYearInt = parseInt(skp.tahun, 10);
                let isRowDisabled = false;

                if (isIntegrasi) {
                    const is4896 = Math.abs(displayAkLama - 48.96) < 0.01;
                    let startYear = 2023;
                    if (is4896 && parsedTmtAsli) {
                        startYear = parsedTmtAsli.year;
                    }
                    // Batas akhir Integrasi (Buka kunci 2026 jika dicentang)
                    let endYear = getVal('is_skp_2026') === 'Ya' ? 2026 : 2025;
                    if (currentYearInt < startYear || currentYearInt > endYear) { isRowDisabled = true; }
                } else { 
                    const isBaruNaikJenjang = getVal('is_baru_naik_jenjang') === 'Ya' || isTmtGol2425;
                    let startYear = 2022; 
                    
                    let shouldStartFromTmt = false;
                    let forceEnd2025 = false;

                    if (isGol3A) {
                        shouldStartFromTmt = true;
                        forceEnd2025 = true;
                    } 
                    else if (isBaseGol && parsedTmtAsli) {
                        if (!parsedSelesai) {
                            shouldStartFromTmt = true;
                        } else {
                            const tmtDateNum = parsedTmtAsli.year * 100 + parsedTmtAsli.month;
                            const selesaiDateNum = parsedSelesai.year * 100 + parsedSelesai.month;
                            if (tmtDateNum > selesaiDateNum) shouldStartFromTmt = true;
                        }
                        if (isBaruNaikJenjang) shouldStartFromTmt = true;
                    }

                    if (shouldStartFromTmt && parsedTmtAsli) {
                        startYear = parsedTmtAsli.year;
                    } else if (parsedSelesai) {
                        let startMonthNum = parsedSelesai.month + 1;
                        startYear = parsedSelesai.year;
                        if (startMonthNum > 12) { startYear += 1; }
                    }

                    // Batas akhir Konversi (Buka kunci 2026 jika dicentang)
                    let endYear = 2025; 
                    if (getVal('is_skp_2026') === 'Ya') {
                        endYear = 2026;
                    } else if (forceEnd2025) {
                        endYear = 2025;
                    } else if (isJabatanTidakSesuai && parsedTmtBaru) {
                        let endMonthNum;
                        if (parsedTmtBaru.month === 12) { 
                            endYear = parsedTmtBaru.year; 
                        } else { 
                            endMonthNum = parsedTmtBaru.month - 1;
                            endYear = parsedTmtBaru.year; 
                            if (endMonthNum <= 0) { endYear -= 1; } 
                        }
                    }

                    if (currentYearInt < startYear || currentYearInt > endYear) { isRowDisabled = true; }
                }

                return (
                <div key={idx} className={`flex flex-col gap-2 p-2 border border-slate-100 rounded ${isRowDisabled ? 'bg-slate-50' : ''}`}>
                  <div className="flex gap-2">
                    {!isEditingBio ? (
                      <div className={`px-2 py-1.5 border border-transparent rounded text-xs flex-1 flex justify-between min-h-[32px] items-center ${isRowDisabled ? 'bg-transparent opacity-60' : 'bg-slate-50'}`}>
                        <span className="font-bold">
                          {skp.tahun || 'Kosong'} 
                          {(skp.tahun == 2026 && skp.bulan) ? ` (${skp.bulan} Bln)` : ''}
                        </span>
                        <span className="text-slate-600">
                          {isRowDisabled && !skp.kode ? '- Di Luar Periode -' : 
                            (skp.kode === '1' ? 'Amat Baik' : skp.kode === '2' ? 'Baik' : skp.kode === '3' ? 'Cukup' : '- Kosong -')
                          }
                          {isRowDisabled && skp.kode ? ' (Terkunci)' : ''}
                        </span>
                      </div>
                    ) : (
                      <>
                        <input type="number" placeholder="Tahun" value={skp.tahun} onChange={(e) => onSKPChange(idx, 'tahun', e.target.value)} className="w-16 border border-slate-300 rounded p-1.5 text-[10px] outline-none focus:border-blue-500 font-bold" />                          
                        <select value={skp.kode || ''} onChange={(e) => onSKPChange(idx, 'kode', e.target.value)} disabled={isRowDisabled} className="flex-1 border border-slate-300 rounded p-1.5 text-[10px] outline-none focus:border-blue-500 disabled:bg-slate-200 disabled:text-slate-500">
                          <option value="">{isRowDisabled ? '-- Terkunci (Di Luar Periode) --' : '- Kosong -'}</option>
                          <option value="1">Amat Baik</option>
                          <option value="2">Baik</option>
                          <option value="3">Cukup / Kurang Baik</option>
                        </select>
                        
                        {/* TAMPILKAN PILIHAN BULAN JIKA BARIS TAHUN 2026 */}
                        {(skp.tahun == 2026 || idx === 3) && (
                          <select 
                            value={skp.bulan || 12} 
                            onChange={(e) => onSKPChange(idx, 'bulan', parseInt(e.target.value))} 
                            disabled={isRowDisabled}
                            className="w-20 border border-blue-300 rounded p-1.5 text-[10px] outline-none font-bold text-blue-900 bg-blue-50 disabled:opacity-50"
                          >
                            {[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>{i+1} Bln</option>)}
                          </select>
                        )}
                      </>
                    )}
                  </div>
                  {currentYearInt < 2026 && !isRowDisabled && (
                    <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
                       <input 
                          type="checkbox" 
                          disabled={!isEditingBio}
                          checked={skp.sudah_pak === true} 
                          onChange={(e) => onSKPChange(idx, 'sudah_pak', e.target.checked)} 
                          className="w-3 h-3 text-blue-600 cursor-pointer"
                       />
                       <span className="text-[9px] font-medium text-slate-500">Pernah ambil PAK (Abaikan cetak SKP ini)</span>
                    </div>
                  )}
                </div>
              )})}
            </div>
          </div>
          <SectionTitle title="Identitas Pribadi" icon={<User size={16} className="text-amber-500"/>} />
          <DataRow label="Nama Lengkap" isEdit={isEditingBio} name="full_name" valEdit={getVal('full_name')} onChange={onBioChange} value={selectedItem.full_name} />
          <DataRow label="NIP" isEdit={false} name="nip" value={selectedItem.nip} />
          <DataRow label="No. Karpeg" isEdit={isEditingBio} name="karpeg" valEdit={getVal('karpeg')} onChange={onBioChange} value={selectedItem.karpeg} />
          
          <div className="grid grid-cols-2 gap-3">
            <DataRow label="Tempat Lahir" isEdit={isEditingBio} name="tempat_lahir" valEdit={getVal('tempat_lahir')} onChange={onBioChange} value={selectedItem.tempat_lahir} />
            <DataRow label="Tanggal Lahir" isEdit={isEditingBio} name="tanggal_lahir" value={formatDateStr(selectedItem.tanggal_lahir)}>
               <CustomDatePicker name="tanggal_lahir" value={getIsoDate(getVal('tanggal_lahir'))} onChange={onBioChange} />
            </DataRow>
          </div>
          
          <DataRow label="Jenis Kelamin" isEdit={isEditingBio} name="jenis_kelamin" value={selectedItem.jenis_kelamin || '-'}>
             <select name="jenis_kelamin" value={getVal('jenis_kelamin')} onChange={onBioChange} className="w-full border rounded p-2 text-sm outline-none focus:border-blue-500">
               <option value="">- Pilih -</option>
               <option value="Laki-laki">Laki-laki</option>
               <option value="Perempuan">Perempuan</option>
             </select>
          </DataRow>

          <SectionTitle title="Pendidikan" icon={<GraduationCap size={16} className="text-emerald-500"/>} />
          <DataRow label="Pendidikan Terakhir" isEdit={isEditingBio} name="pendidikan_pangkat" valEdit={getVal('pendidikan_pangkat')} onChange={onBioChange} value={selectedItem.pendidikan_pangkat} />
          <DataRow label="Gelar Baru?" isEdit={isEditingBio} name="is_pencantuman_gelar" value={selectedItem.is_pencantuman_gelar}>
             <select name="is_pencantuman_gelar" value={getVal('is_pencantuman_gelar') || 'Tidak'} onChange={onBioChange} className="w-full border rounded p-2 text-sm outline-none focus:border-blue-500">
               <option value="Ya">Ya</option><option value="Tidak">Tidak</option>
             </select>
          </DataRow>

          {(getVal('is_pencantuman_gelar') === 'Ya') && (
             <div className="grid grid-cols-1 gap-3 bg-emerald-50 p-3 rounded-lg">
               <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                 <DataRow label="Tingkat Gelar Baru" isEdit={isEditingBio} name="pendidikan_gelar_baru" value={selectedItem.pendidikan_gelar_baru}>
                   <select name="pendidikan_gelar_baru" value={getVal('pendidikan_gelar_baru')} onChange={onBioChange} className="w-full border rounded p-2 text-sm outline-none focus:border-blue-500">
                     <option value="">-- Pilih --</option><option value="S1">S1</option><option value="S2">S2</option><option value="S3">S3</option>
                  </select>
                 </DataRow>
                 <DataRow label="Nama Prodi/Jurusan" isEdit={isEditingBio} name="nama_pendidikan_baru" valEdit={getVal('nama_pendidikan_baru')} onChange={onBioChange} value={selectedItem.nama_pendidikan_baru || '-'} />
               </div>
               <div className="grid grid-cols-3 gap-3 border-t border-emerald-200 pt-2">
                 <DataRow label="Tahun Lulus" isEdit={isEditingBio} name="tahun_gelar_baru" type="number" valEdit={getVal('tahun_gelar_baru')} onChange={onBioChange} value={selectedItem.tahun_gelar_baru} />
                 <DataRow label="Thn Usulan" isEdit={isEditingBio} name="tahun_usulan_gelar" type="number" valEdit={getVal('tahun_usulan_gelar')} onChange={onBioChange} value={selectedItem.tahun_usulan_gelar} />
                 <DataRow label="Nilai AK" isEdit={isEditingBio} name="ak_pg" type="number" value={<span className="font-bold text-emerald-600">{selectedItem.ak_pg || 0}</span>}>
                   <input type="number" name="ak_pg" value={getVal('ak_pg') || 0} readOnly className="w-full border border-transparent bg-slate-100 text-emerald-700 font-bold rounded p-2 text-sm cursor-not-allowed outline-none" />
                 </DataRow>
               </div>
             </div>
          )}

          <SectionTitle title="Kepangkatan & Jabatan" icon={<FileBadge size={16} className="text-purple-500"/>} />
          
          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
             <label className="text-amber-900 text-[10px] font-bold uppercase block mb-2">Jenis Jabatan Fungsional</label>
             <div className="flex gap-4">
               <label className="flex items-center gap-1 text-sm cursor-pointer">
                 <input type="radio" name="jenis_jabatan" value="Guru" checked={!isNonGuru} onChange={onBioChange} disabled={!isEditingBio} className="w-4 h-4 text-amber-600" /> Guru
               </label>
               <label className="flex items-center gap-1 text-sm cursor-pointer">
                 <input type="radio" name="jenis_jabatan" value="Non-Guru" checked={isNonGuru} onChange={onBioChange} disabled={!isEditingBio} className="w-4 h-4 text-amber-600" /> Non-Guru
               </label>
             </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
         <DataRow label="Golongan" isEdit={isEditingBio} name="golongan" value={selectedItem.golongan}>
  <select name="golongan" value={getVal('golongan')} onChange={handleGolonganChangeLocal} className="w-full border rounded p-2 text-sm outline-none focus:border-blue-500">
    <option value="">- Pilih Gol -</option>
    {golonganOptions.map(gol => <option key={gol} value={gol}>{gol}</option>)}
  </select>
</DataRow>
             <DataRow label="TMT Golongan" isEdit={isEditingBio} name="tmt_golongan" value={formatDateStr(selectedItem.tmt_golongan)}>
               <CustomDatePicker name="tmt_golongan" value={getIsoDate(getVal('tmt_golongan'))} onChange={onBioChange} />
             </DataRow>
          </div>

          {isGolKhususReset && (
              <div className="mb-3 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                  {isTmtGol2425 ? (
                      <div className="flex items-start gap-2">
                          <span className="text-sm font-bold text-rose-900 leading-tight">
                              TMT Golongan ≥ 2024 (Otomatis Reset AK)
                              <span className="block text-xs font-normal text-rose-700 mt-0.5">Sistem mendeteksi kenaikan pangkat pada tahun {parsedTmtGol.year}. Sesuai aturan, Angka Kredit Sebelumnya otomatis di-reset menjadi 0.</span>
                          </span>
                      </div>
                  ) : (
                      <label className="flex items-start gap-2 cursor-pointer">
                          <input type="checkbox" name="is_baru_naik_jenjang" checked={getVal('is_baru_naik_jenjang') === 'Ya'} onChange={(e) => onBioChange({target: {name: 'is_baru_naik_jenjang', value: e.target.checked ? 'Ya' : 'Tidak'}})} className="mt-1 w-4 h-4 text-rose-600 cursor-pointer" disabled={!isEditingBio} />
                          <span className="text-sm font-bold text-rose-900 leading-tight">
                              Pegawai baru naik jenjang jabatan ke {getVal('golongan')}?
                              <span className="block text-xs font-normal text-rose-700 mt-0.5">Centang jika "Ya" (Sesuai aturan, Angka Kredit Sebelumnya akan di-reset menjadi 0).</span>
                          </span>
                      </label>
                  )}
              </div>
          )}

          <div className="mb-3">
            <label className="text-slate-500 text-[10px] font-bold uppercase block mb-1">Masa Kerja</label>
            {!isEditingBio ? (
               <div className="font-medium text-slate-800 text-sm bg-slate-50 p-2 rounded min-h-[36px] flex items-center">{selectedItem.masa_kerja_tahun || 0} Thn, {selectedItem.masa_kerja_bulan || 0} Bln</div>
            ) : (
               <div className="flex gap-2">
                 <div className="flex flex-1 items-center gap-2"><input type="number" name="masa_kerja_tahun" value={getVal('masa_kerja_tahun')} onChange={onBioChange} className="w-full border rounded p-2 text-sm outline-none focus:border-blue-500" placeholder="Thn"/></div>
                 <div className="flex flex-1 items-center gap-2"><input type="number" name="masa_kerja_bulan" value={getVal('masa_kerja_bulan')} onChange={onBioChange} className="w-full border rounded p-2 text-sm outline-none focus:border-blue-500" placeholder="Bln"/></div>
               </div>
            )}
          </div>

          <DataRow label="JABATAN FUNGSIONAL" isEdit={isEditingBio} name="jabatan" valEdit={getVal('jabatan')} onChange={onBioChange} value={selectedItem.jabatan}>
             {!isNonGuru ? (
                <input type="text" name="jabatan" value={getVal('jabatan') || ''} readOnly className="w-full border border-transparent bg-slate-100 text-slate-700 rounded px-3 py-2 text-sm cursor-not-allowed outline-none" />
             ) : (
                <input type="text" name="jabatan" value={getVal('jabatan') || ''} onChange={onBioChange} placeholder="Ketik jabatan faktual..." className="w-full border border-slate-300 rounded px-3 py-2 text-sm outline-none focus:border-blue-500" />
             )}
          </DataRow>

          <DataRow label="TMT Jabatan" isEdit={isEditingBio} name="tmt_jabatan" value={formatDateStr(selectedItem.tmt_jabatan)}>
             <CustomDatePicker name="tmt_jabatan" value={getIsoDate(getVal('tmt_jabatan'))} onChange={onBioChange} />
          </DataRow>

          <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg mb-3">
            <DataRow label="JABATAN SUDAH SESUAI?" isEdit={isEditingBio} name="is_jabatan_sesuai" value={selectedItem.is_jabatan_sesuai}>
              <select name="is_jabatan_sesuai" value={getVal('is_jabatan_sesuai') || 'Ya'} onChange={onBioChange} className="w-full border rounded p-2 text-sm outline-none focus:border-blue-500">
                <option value="Ya">Ya, Sesuai</option><option value="Tidak">Belum (Masih Jabatan Lama)</option>
              </select>
            </DataRow>
            {(getVal('is_jabatan_sesuai') === 'Tidak') && (
              <div className="grid grid-cols-2 gap-3 mt-2 pt-2 border-t border-orange-200">
                  <DataRow label="Jabatan Baru" isEdit={isEditingBio} name="jabatan_baru" value={selectedItem.jabatan_baru}>
                   {!isNonGuru ? (
                     <select name="jabatan_baru" value={getVal('jabatan_baru')} onChange={onBioChange} className="w-full border rounded p-2 text-sm outline-none focus:border-blue-500">
                        <option value="">- Pilih -</option>
                        <option value="Guru Ahli Pertama">Ahli Pertama</option>
                        <option value="Guru Ahli Muda">Ahli Muda</option>
                        <option value="Guru Ahli Madya">Ahli Madya</option>
                        <option value="Guru Ahli Utama">Ahli Utama</option>
                     </select>
                   ) : (
                     <input type="text" name="jabatan_baru" value={getVal('jabatan_baru') || ''} onChange={onBioChange} placeholder="Jabatan Baru..." className="w-full border border-slate-300 rounded px-3 py-2 text-sm outline-none focus:border-blue-500" />
                   )}
                </DataRow>
                <DataRow label="TMT Jabatan Baru" isEdit={isEditingBio} name="tmt_jabatan_baru" value={formatDateStr(selectedItem.tmt_jabatan_baru)}>
                  <CustomDatePicker name="tmt_jabatan_baru" value={getIsoDate(getVal('tmt_jabatan_baru'))} onChange={onBioChange} />
                </DataRow>
              </div>
            )}
          </div>

          <DataRow label="Unit Kerja" isEdit={isEditingBio} name="unit_kerja" valEdit={getVal('unit_kerja')} onChange={onBioChange} value={selectedItem.unit_kerja || '-'} />
          
          {isEditingBio && (
            <div className="mt-6 pt-4 border-t border-slate-200 pb-2">
               <button onClick={onSaveBio} className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold flex justify-center items-center gap-2 shadow-md transition-all text-base">
                  <Save size={20} /> SIMPAN PERUBAHAN DATA USULAN
                </button>
            </div>
          )}

        </div>
      </div>

      {userRole !== 'satlak' && (
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2 border-b pb-2">
            <Download size={18} className="text-emerald-600"/> Dokumen Lampiran
          </h3>
          <div className="space-y-2">
            {selectedItem.dokumen && selectedItem.dokumen.length > 0 ? (
              selectedItem.dokumen.map((doc, i) => (
                <button key={i} type="button" onClick={() => onPreviewClick(doc.url)} className="flex items-center w-full justify-between p-2.5 rounded border border-slate-200 hover:bg-slate-50 transition-all group">
                  <span className="text-[11px] font-bold text-slate-700 group-hover:text-emerald-700 truncate mr-2">{doc.nama || `Lampiran ${i+1}`}</span>
                  <Eye size={14} className="text-slate-400 group-hover:text-emerald-600 flex-shrink-0" />
                </button>
              ))
            ) : (
              <div className="text-center p-3 text-xs text-slate-400 bg-slate-50 rounded border border-dashed">Tidak ada dokumen</div>
            )}
          </div>
        </div>
      )}

      {!isReadOnly && (
        <div className="flex gap-3 pb-6 xl:pb-0 mt-6 pt-4 border-t border-slate-200">
          
          <button 
            onClick={onReject}
            disabled={selectedItem?.status?.toLowerCase() === 'ditolak'}
            className={`flex-1 py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-all ${
              selectedItem?.status?.toLowerCase() === 'ditolak'
                ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed opacity-70' 
                : 'bg-white text-rose-600 border border-rose-200 hover:bg-rose-50 hover:border-rose-300 shadow-sm hover:shadow-md'
            }`}
          >
            <XCircle size={18} /> {selectedItem?.status?.toLowerCase() === 'ditolak' ? 'Telah Ditolak' : 'Reject'}
          </button>

          <button 
            onClick={onApprove}
            disabled={selectedItem?.status?.toLowerCase() === 'disetujui'}
            className={`flex-1 py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-all ${
              selectedItem?.status?.toLowerCase() === 'disetujui' 
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed shadow-none' 
                : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/30'
            }`}
          >
            <CheckCircle size={18} /> 
            {selectedItem?.status?.toLowerCase() === 'disetujui' ? 'Telah Disetujui' : 'Approve'}
          </button>

        </div>
      )}
    </div>
  );
};

export default DetailPegawaiCard;